// Small country and region flags as inline SVG: flag emoji render as two letters in Excel's webview on Windows.
import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';

const W = 16;
const H = 12;

/** US: 13 stripes, a blue canton with a few stars suggested. */
function US() {
  const stripe = H / 13;
  const canton = stripe * 7;
  return (
    <>
      <rect width={W} height={H} fill="#fff" />
      {Array.from({ length: 7 }, (_, i) => (
        <rect key={i} y={i * 2 * stripe} width={W} height={stripe} fill="#B22234" />
      ))}
      <rect width={6.4} height={canton} fill="#3C3B6E" />
      {[0, 1, 2].flatMap((row) => [0, 1, 2].map((col) => <circle key={`${row}-${col}`} cx={1.1 + col * 2.1 + (row % 2) * 0.5} cy={1.2 + row * 2.2} r={0.35} fill="#fff" />))}
    </>
  );
}

function FR() {
  return (
    <>
      <rect width={W / 3} height={H} fill="#002654" />
      <rect x={W / 3} width={W / 3} height={H} fill="#fff" />
      <rect x={(2 * W) / 3} width={W / 3} height={H} fill="#CE1126" />
    </>
  );
}

/** EU: twelve gold stars in a circle on blue. */
function EU() {
  return (
    <>
      <rect width={W} height={H} fill="#003399" />
      {Array.from({ length: 12 }, (_, i) => {
        const a = (i * Math.PI) / 6;
        return <circle key={i} cx={W / 2 + 3.6 * Math.sin(a)} cy={H / 2 - 3.6 * Math.cos(a)} r={0.55} fill="#FFCC00" />;
      })}
    </>
  );
}

const FLAGS: Record<string, () => JSX.Element> = { US, FR, EU };

/** The flag of `code` (ISO 3166-1 alpha-2, or `EU`), ~16×12 with rounded corners and a thin border; a code without a
 *  drawing shows as its letters in a small mono badge. Named by `flag.<code>` in the locale, else the code. */
export function Flag({ code, className = '' }: { code: string; className?: string }) {
  const { t } = useTranslation();
  const upper = code.toUpperCase();
  const name = t(`flag.${upper}`, { defaultValue: upper });
  const Draw = FLAGS[upper];
  if (!Draw)
    return (
      <span title={name} aria-label={name} className={`inline-block rounded-[3px] border border-line px-1 font-mono text-[10px] leading-[14px] text-muted ${className}`}>
        {upper}
      </span>
    );
  return (
    <span role="img" title={name} aria-label={name} className={`inline-flex shrink-0 overflow-hidden rounded-[2px] ring-1 ring-black/15 ${className}`}>
      <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} aria-hidden="true" focusable="false" className="block">
        <Draw />
      </svg>
    </span>
  );
}
